import type { DragEndEvent } from '@dnd-kit/core';
import React from 'react';

interface UseDragAndDropParams {
  pageGroups: number[][];
  onReorderPages?: (newGroups: number[][]) => void;
}

interface UseDragAndDropResult {
  handleDragEnd: (e: DragEndEvent) => void;
}

export const useDragAndDrop = ({
  pageGroups,
  onReorderPages,
}: UseDragAndDropParams): UseDragAndDropResult => {
  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const draggedPage = event.active.id as number;
      const targetPage = event.over?.id as number | undefined;

      if (!targetPage || draggedPage === targetPage || !onReorderPages) {
        return;
      }

      const draggedGroupIndex = pageGroups.findIndex((group) =>
        group.includes(draggedPage)
      );
      const targetGroupIndex = pageGroups.findIndex((group) =>
        group.includes(targetPage)
      );

      // Only reorder within same group
      if (draggedGroupIndex !== targetGroupIndex || draggedGroupIndex === -1) {
        return;
      }

      const newGroups = [...pageGroups];
      const group = [...newGroups[draggedGroupIndex]];

      const draggedIndex = group.indexOf(draggedPage);
      const targetIndex = group.indexOf(targetPage);

      // Reorder within the group
      group.splice(draggedIndex, 1);
      group.splice(targetIndex, 0, draggedPage);

      newGroups[draggedGroupIndex] = group;
      if (JSON.stringify(newGroups) !== JSON.stringify(pageGroups)) {
        onReorderPages(newGroups);
      }
    },
    [pageGroups, onReorderPages]
  );

  return {
    handleDragEnd,
  };
};
